import { useState, useEffect, useRef, useCallback, useId } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Bot, Send, X, ExternalLink, ArrowRight, AlertTriangle, Loader2 } from 'lucide-react'
import {
  askAssistant, getAssistantTopics, ASSISTANT_CATEGORY_LABELS,
  type AssistantAnswer, type AssistantTopic,
} from '../../services/assistantService'

// ================================================================================
// FLOATING TB ASSISTANT — homepage floating widget.
//
// This does NOT reimplement the chatbot. It is a presentation layer over the
// EXISTING controlled retrieval assistant: it calls the same `askAssistant()`
// and `getAssistantTopics()` used by the full `/assistant` page, and renders the
// same `AssistantAnswer` shape (category, emergencyNote, officialSourceUrl,
// portalLinkPath, suggestions). No new API, no duplicated logic, no second
// chatbot. The full `/assistant` page (TbAssistantPage) remains untouched and is
// still linked from the mobile bottom nav.
//
// Behaviour:
//  - Collapsed by default → a premium floating button (bottom-right).
//  - Optional one-time intro bubble ("Need help with TB?"), dismissed for the
//    session (sessionStorage).
//  - Click opens a compact chat window above the button.
//  - Quick actions send a natural-language query to `askAssistant` (the answer's
//    `portalLinkPath` provides in-chat navigation — "Find a TB Centre →", etc.).
//  - Clears the mobile `PortalBottomNav` (xl:hidden, fixed bottom-0) on <xl by
//    sitting above it; the FAB hides on mobile while the panel is open so the
//    panel can use that space. On mobile the keyboard is handled via
//    `visualViewport` so the input stays visible and the layout never breaks.
// ================================================================================

interface Message {
  role: 'user' | 'assistant'
  text: string
  answer?: AssistantAnswer
}

const QUICK_ACTIONS: { icon: string; query: string; labelKey: string; labelDefault: string }[] = [
  { icon: '🩺', query: 'What are the symptoms of TB?', labelKey: 'pages.assistant.fab.qaSymptoms', labelDefault: 'TB Symptoms' },
  { icon: '📍', query: 'How can I find a TB centre near me?', labelKey: 'pages.assistant.fab.qaFindCentre', labelDefault: 'Find TB Centre' },
  { icon: '💊', query: 'What is the treatment for TB?', labelKey: 'pages.assistant.fab.qaTreatment', labelDefault: 'TB Treatment' },
  { icon: '🥗', query: 'What nutrition support is available for TB patients?', labelKey: 'pages.assistant.fab.qaNutrition', labelDefault: 'Nutrition Support' },
  { icon: '🤝', query: 'How can I support someone with TB?', labelKey: 'pages.assistant.fab.qaSupport', labelDefault: 'Support a TB Patient' },
  { icon: '❓', query: 'What is TB?', labelKey: 'pages.assistant.fab.qaGeneral', labelDefault: 'General TB Questions' },
]

const INTRO_KEY = 'tb-assistant-intro-dismissed'

export function FloatingTbAssistant() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [topics, setTopics] = useState<AssistantTopic[]>([])
  const [showIntro, setShowIntro] = useState(false)

  const logRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fabRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const panelId = useId()

  // ---- intro bubble: show once per session until dismissed ----
  useEffect(() => {
    try {
      if (sessionStorage.getItem(INTRO_KEY) !== '1') {
        // small delay so it doesn't flash during first paint
        const id = setTimeout(() => setShowIntro(true), 1200)
        return () => clearTimeout(id)
      }
    } catch { /* sessionStorage may be unavailable */ }
  }, [])

  const dismissIntro = useCallback(() => {
    setShowIntro(false)
    try { sessionStorage.setItem(INTRO_KEY, '1') } catch { /* ignore */ }
  }, [])

  // ---- welcome message + topic chips (reuse existing API) ----
  useEffect(() => {
    getAssistantTopics().then(setTopics)
  }, [])

  useEffect(() => {
    setMessages([{
      role: 'assistant',
      text: t('pages.assistant.greeting', {
        defaultValue: "Hi, I'm the TB Assistant. I can answer questions about TB symptoms, testing, treatment, becoming a Ni-Kshay Mitra, patient support and finding your nearest TB centre. How can I help?",
      }),
    }])
  }, [t])

  // ---- auto-scroll ----
  useEffect(() => {
    const el = logRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages, loading, open])

  // ---- send a question via the EXISTING assistant API ----
  const send = useCallback(async (text: string) => {
    const query = text.trim()
    if (!query || loading) return
    setInput('')
    setMessages((m) => [...m, { role: 'user', text: query }])
    setLoading(true)
    try {
      const answer = await askAssistant(query)
      setMessages((m) => [...m, { role: 'assistant', text: answer.answer, answer }])
    } catch {
      setMessages((m) => [...m, {
        role: 'assistant',
        text: t('pages.assistant.error', { defaultValue: 'Sorry, I could not reach the assistant right now. Please try again in a moment.' }),
      }])
    } finally {
      setLoading(false)
    }
  }, [loading, t])

  const onSubmit = (ev: React.FormEvent) => { ev.preventDefault(); send(input) }

  // ---- open / close with focus management + ESC ----
  const openPanel = useCallback(() => {
    setOpen(true)
    setShowIntro(false)
    // focus the input shortly after the panel animates in
    setTimeout(() => inputRef.current?.focus(), 220)
  }, [])

  const closePanel = useCallback(() => {
    setOpen(false)
    // return focus to the trigger for keyboard users
    setTimeout(() => fabRef.current?.focus(), 50)
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); closePanel() }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, closePanel])

  // ---- mobile keyboard handling via visualViewport ----
  // Fixed elements are anchored to the layout viewport, so when the soft
  // keyboard opens the input would slide behind it. We re-anchor the panel's
  // bottom to the top of the keyboard and cap its height to the visible area.
  const [mobileStyle, setMobileStyle] = useState<{ bottom: number; height: number } | null>(null)
  useEffect(() => {
    if (!open) { setMobileStyle(null); return }
    const vv = typeof window !== 'undefined' ? window.visualViewport : null
    if (!vv) return
    const update = () => {
      if (window.innerWidth >= 1024) { setMobileStyle(null); return }
      const kb = window.innerHeight - (vv.height + vv.offsetTop)
      if (kb > 24) {
        setMobileStyle({ bottom: kb + 8, height: Math.max(300, vv.height - 16) })
      } else {
        setMobileStyle(null)
      }
    }
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    window.addEventListener('resize', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [open])

  const greetingOnly = messages.length <= 1

  return (
    <>
      {/* ===================== FLOATING BUTTON ===================== */}
      <button
        ref={fabRef}
        type="button"
        onClick={() => (open ? closePanel() : openPanel())}
        aria-label={open
          ? t('pages.assistant.fab.closeLabel', { defaultValue: 'Close TB Assistant' })
          : t('pages.assistant.fab.openLabel', { defaultValue: 'Open TB Assistant' })}
        aria-expanded={open}
        aria-controls={panelId}
        className={[
          'fixed z-[100] right-4 bottom-[calc(72px+env(safe-area-inset-bottom))] xl:right-6 xl:bottom-6',
          'w-[56px] h-[56px] xl:w-[60px] xl:h-[60px] rounded-full',
          'flex items-center justify-center',
          'bg-gradient-to-br from-[rgb(15_76_129)] to-[rgb(13_148_136)] text-white',
          'shadow-lg hover:shadow-xl hover:scale-105 active:scale-95',
          'transition-all duration-200 outline-none focus-visible:ring-4 focus-visible:ring-[rgb(15_76_129)]/30',
          // hide on mobile while the panel is open (panel reuses that space);
          // keep visible on desktop where the panel sits above it.
          open ? 'opacity-0 pointer-events-none translate-y-2 scale-90 xl:opacity-100 xl:pointer-events-auto xl:translate-y-0 xl:scale-100' : 'opacity-100',
        ].join(' ')}
      >
        {/* Closed → Bot; open → X (desktop shows it; on mobile the button is
            hidden while the panel is open, so the icon is irrelevant there). */}
        {open ? <X className="w-6 h-6" /> : <Bot className="w-6 h-6" />}
      </button>

      {/* ===================== INTRO BUBBLE ===================== */}
      {showIntro && !open && (
        <div
          role="status"
          className="fixed z-[100] right-[80px] bottom-[calc(72px+env(safe-area-inset-bottom))] xl:right-[84px] xl:bottom-4 max-w-[210px] mb-1"
        >
          <div className="relative bg-white text-snow-800 text-sm shadow-lg rounded-xl border border-snow-200 px-3 py-2 pr-7">
            {t('pages.assistant.fab.intro', { defaultValue: 'Need help with TB?' })}
            {/* pointer toward the button */}
            <span className="absolute -right-1.5 bottom-3 w-3 h-3 bg-white border-r border-b border-snow-200 rotate-[-45deg]" aria-hidden="true" />
            <button
              type="button"
              onClick={dismissIntro}
              aria-label={t('common.dismiss', { defaultValue: 'Dismiss' })}
              className="absolute top-1 right-1 p-1 rounded text-snow-400 hover:text-snow-700 hover:bg-snow-100 min-w-[28px] min-h-[28px] flex items-center justify-center"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* ===================== CHAT PANEL ===================== */}
      <div
        ref={panelRef}
        id={panelId}
        role="dialog"
        aria-label={t('pages.assistant.fab.headerTitle', { defaultValue: 'TB Assistant' })}
        aria-hidden={!open}
        // tabIndex={-1} removed: dialog is non-modal; focus is managed to the input.
        style={mobileStyle ? { bottom: mobileStyle.bottom, height: mobileStyle.height } : undefined}
        className={[
          'fixed z-[100] right-3 w-[calc(100vw-24px)] max-w-[400px]',
          'bottom-[calc(72px+env(safe-area-inset-bottom))] h-[min(70vh,560px)] max-h-[calc(100vh-72px-env(safe-area-inset-bottom)-8px)]',
          'xl:right-6 xl:w-[380px] xl:max-w-none xl:bottom-[90px] xl:h-[560px] xl:max-h-[calc(100vh-120px)]',
          'flex flex-col bg-white rounded-2xl shadow-2xl border border-snow-200 overflow-hidden origin-bottom-right',
          'transition-all duration-200',
          open ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto' : 'opacity-0 translate-y-3 scale-95 pointer-events-none',
        ].join(' ')}
      >
        {/* ---------- HEADER ---------- */}
        <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[rgb(15_76_129)] to-[rgb(13_148_136)] text-white shrink-0">
          <span className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center shrink-0" aria-hidden="true">
            <Bot className="w-5 h-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold leading-tight truncate">
              {t('pages.assistant.fab.headerTitle', { defaultValue: 'TB Assistant' })}
            </p>
            <p className="text-[11px] text-white/80 leading-tight truncate">
              {t('pages.assistant.fab.headerSubtitle', { defaultValue: 'Your guide to TB information & support' })}
            </p>
          </div>
          <button
            type="button"
            onClick={closePanel}
            aria-label={t('pages.assistant.fab.closeLabel', { defaultValue: 'Close TB Assistant' })}
            className="p-1.5 rounded-lg hover:bg-white/15 transition min-w-[36px] min-h-[36px] flex items-center justify-center focus-visible:ring-2 focus-visible:ring-white/60 outline-none"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ---------- MESSAGE LOG ---------- */}
        <div
          ref={logRef}
          role="log"
          aria-live="polite"
          aria-label={t('pages.assistant.ariaLog', { defaultValue: 'Assistant conversation' })}
          className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-snow-50"
        >
          {/* Quick actions — shown initially (greeting only), reuse the assistant API */}
          {greetingOnly && (
            <div className="pb-1">
              <p className="text-xs font-medium text-snow-500 mb-2">
                {t('pages.assistant.fab.howHelp', { defaultValue: 'How can we help?' })}
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {QUICK_ACTIONS.map((qa) => (
                  <button
                    key={qa.labelKey}
                    type="button"
                    onClick={() => send(qa.query)}
                    className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-white border border-snow-200 hover:border-[rgb(15_76_129)] hover:bg-[rgb(240_252_255)] text-xs text-snow-700 text-left transition min-h-[40px] focus-visible:ring-2 focus-visible:ring-[rgb(15_76_129)]/40 outline-none"
                  >
                    <span aria-hidden="true" className="text-sm leading-none">{qa.icon}</span>
                    <span className="leading-tight">{t(qa.labelKey, { defaultValue: qa.labelDefault })}</span>
                  </button>
                ))}
              </div>
              {/* Curated topic chips (existing API) as further suggestions */}
              {topics.length > 0 && (
                <div className="mt-3">
                  <p className="text-[11px] text-snow-500 mb-1.5">
                    {t('pages.assistant.tryAsking', { defaultValue: 'Try asking:' })}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {topics.slice(0, 6).map((tp) => (
                      <button
                        key={tp.id}
                        type="button"
                        onClick={() => send(tp.title)}
                        className="px-2.5 py-1 rounded-full bg-snow-100 hover:bg-snow-200 text-[11px] text-snow-700 min-h-[32px]"
                      >
                        {tp.title}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[88%] ${m.role === 'user' ? '' : 'flex gap-2'}`}>
                {m.role === 'assistant' && (
                  <div className="shrink-0 w-7 h-7 rounded-full bg-[rgb(15_76_129)] text-white flex items-center justify-center mt-0.5" aria-hidden="true">
                    <Bot className="w-4 h-4" />
                  </div>
                )}
                <div className={m.role === 'user'
                  ? 'bg-[rgb(15_76_129)] text-white rounded-2xl rounded-br-md px-3 py-2 text-[13px]'
                  : 'bg-white border border-snow-100 rounded-2xl rounded-bl-md px-3 py-2 text-[13px] text-snow-800 shadow-sm'}>
                  <p className="whitespace-pre-wrap">{m.text}</p>

                  {/* Assistant answer extras — same shape as the full page */}
                  {m.answer && (
                    <div className="mt-2 space-y-2">
                      {m.answer.category && (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-[rgb(232_241_252)] text-[rgb(15_76_129)] text-[11px] font-medium">
                          {t(`pages.assistant.categories.${m.answer.category}`, { defaultValue: ASSISTANT_CATEGORY_LABELS[m.answer.category] ?? m.answer.category })}
                        </span>
                      )}
                      {m.answer.emergencyNote && (
                        <div role="alert" className="flex items-start gap-2 p-2 rounded-lg bg-[rgb(254_242_242)] border border-[rgb(252_165_165)] text-[rgb(153_27_27)] text-[11px]">
                          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
                          <span>{m.answer.emergencyNote}</span>
                        </div>
                      )}
                      {(m.answer.officialSourceUrl || m.answer.portalLinkPath) && (
                        <div className="flex flex-col items-start gap-1.5 pt-0.5">
                          {m.answer.officialSourceUrl && (
                            <a href={m.answer.officialSourceUrl} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] font-medium text-[rgb(15_76_129)] hover:underline min-h-[32px]">
                              {m.answer.officialSourceLabel || t('pages.assistant.officialSource', { defaultValue: 'Visit official source' })}
                              <ExternalLink className="w-3 h-3" aria-hidden="true" />
                            </a>
                          )}
                          {m.answer.portalLinkPath && (
                            <Link to={m.answer.portalLinkPath}
                              className="inline-flex items-center gap-1 text-[11px] font-medium text-[rgb(0_152_186)] hover:underline min-h-[32px]">
                              {m.answer.portalLinkLabel || t('pages.assistant.openPortal', { defaultValue: 'Open on portal' })}
                              <ArrowRight className="w-3 h-3" aria-hidden="true" />
                            </Link>
                          )}
                        </div>
                      )}
                      {m.answer.matched === false && m.answer.suggestions && m.answer.suggestions.length > 0 && (
                        <div className="pt-0.5">
                          <p className="text-[11px] text-snow-500 mb-1">{t('pages.assistant.tryAsking', { defaultValue: 'Try asking:' })}</p>
                          <div className="flex flex-wrap gap-1.5">
                            {m.answer.suggestions.slice(0, 5).map((s) => (
                              <button key={s.id} type="button" onClick={() => send(s.title)}
                                className="px-2 py-0.5 rounded-full bg-snow-100 hover:bg-snow-200 text-[11px] text-snow-700 min-h-[28px]">
                                {s.title}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="flex gap-2">
                <div className="shrink-0 w-7 h-7 rounded-full bg-[rgb(15_76_129)] text-white flex items-center justify-center mt-0.5" aria-hidden="true">
                  <Bot className="w-4 h-4" />
                </div>
                <div className="bg-white border border-snow-100 rounded-2xl rounded-bl-md px-3 py-2.5 shadow-sm">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-snow-400" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ---------- INPUT ---------- */}
        <form onSubmit={onSubmit} className="flex items-end gap-2 p-2.5 border-t border-snow-100 bg-white shrink-0">
          <label htmlFor="tb-fab-input" className="sr-only">
            {t('pages.assistant.inputLabel', { defaultValue: 'Ask a question' })}
          </label>
          <textarea
            id="tb-fab-input"
            ref={inputRef}
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input) } }}
            placeholder={t('pages.assistant.placeholder', { defaultValue: 'Ask about symptoms, testing, treatment, support…' })}
            className="flex-1 resize-none max-h-28 min-h-[40px] px-3 py-2 text-sm rounded-lg border border-snow-200 bg-snow-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[rgb(15_76_129)]/40"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            aria-label={t('pages.assistant.send', { defaultValue: 'Send' })}
            className="shrink-0 w-10 h-10 rounded-lg bg-[rgb(15_76_129)] text-white flex items-center justify-center hover:bg-[rgb(13_64_108)] disabled:opacity-40 disabled:cursor-not-allowed transition focus-visible:ring-2 focus-visible:ring-[rgb(15_76_129)]/40 outline-none"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>
      </div>
    </>
  )
}

export default FloatingTbAssistant